import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, FileSearch, MessageSquare, Workflow } from 'lucide-react';
import SEO from '../components/SEO';
import { Section } from '../components/Section';
import { buildFAQSchema, GeoSeoSections } from '../components/GeoSeoSections';

const faqs = [
  {
    question: '¿Qué puede hacer un agente IA para empresas en Perú?',
    answer: 'Puede apoyar seguimiento comercial, clasificación de leads, respuesta inicial, organización documental, reporting y coordinación de tareas repetitivas.'
  },
  {
    question: '¿Qué diferencia hay entre un chatbot simple y un sistema de agentes?',
    answer: 'Un sistema de agentes no solo conversa: también ayuda a ordenar procesos, activar acciones, mover información útil y sostener continuidad operativa.'
  },
  {
    question: '¿Esto sirve para empresas peruanas fuera de Lima?',
    answer: 'Sí. El enfoque se puede adaptar a equipos comerciales, consultoras, clínicas, negocios educativos y empresas de servicios en distintas ciudades del Perú.'
  },
  {
    question: '¿Neuralia369 conecta agentes con procesos reales?',
    answer: 'Sí. La propuesta combina agentes IA con automatización, criterio humano y una capa de control para que la operación sea más útil y medible.'
  }
];

const successCases = [
  {
    title: 'Empresa con seguimiento comercial irregular',
    copy: 'Agentes orientados a ordenar conversaciones, recordar próximos pasos y sostener continuidad entre lead, propuesta y cierre.',
    outcome: 'más disciplina comercial y más respuesta'
  },
  {
    title: 'Negocio que trabaja con muchos documentos y validaciones',
    copy: 'Organización de información, lectura operativa y apoyo documental para reducir tiempos muertos y búsquedas manuales.',
    outcome: 'más agilidad en la operación diaria'
  },
  {
    title: 'Equipo con tareas repartidas entre varias personas',
    copy: 'Orquestación de agentes y procesos para que cada responsable vea qué toca hacer, qué sigue y qué está bloqueando resultados.',
    outcome: 'más coordinación y menos desorden'
  }
];

const sectors = [
  { badge: 'Perú', title: 'Consultoría y servicios', copy: 'Ideal para negocios que venden por relación, seguimiento y propuesta.' },
  { badge: 'Perú', title: 'Educación y tesis', copy: 'Muy útil cuando hay acompañamiento, documentación, entregables y observaciones.' },
  { badge: 'Perú', title: 'Clínicas y atención', copy: 'Encaja en flujos con pacientes, leads, citas y coordinación operativa.' },
  { badge: 'Perú', title: 'Empresas comerciales', copy: 'Aporta claridad para responder más rápido y mover mejor cada oportunidad.' }
];

const localSignals = [
  'Pensado para empresas en Perú que quieren usar IA con enfoque práctico, no solo como demo aislada.',
  'Útil cuando el reto principal es sostener seguimiento, ordenar tareas y no perder información entre canales.',
  'Puede convivir con ventas, documentos, reporting, comunicación y capas de automatización dentro del mismo sistema.',
  'Encaja especialmente bien en negocios donde la rapidez de respuesta y la trazabilidad comercial influyen en el cierre.'
];

const faqSchema = buildFAQSchema(faqs);

export default function AgentesPeruPage() {
  return (
    <>
      <SEO
        title="Agentes IA para empresas en Perú | Neuralia369"
        description="Agentes IA para empresas en Perú: ventas, seguimiento, reporting, documentos y automatización con Neuralia369."
        path="/agentes-ia-empresas-peru"
        keywords="agentes IA Perú, automatización empresas Perú, IA para empresas Perú, seguimiento comercial IA Perú"
        schemas={[faqSchema]}
      />
      <div className="min-h-screen bg-background pt-24 pb-20" data-testid="agentes-peru-page">
        <Section>
          <div className="max-w-6xl mx-auto grid gap-10 xl:grid-cols-[1.03fr_0.97fr]">
            <div>
              <p className="editorial-kicker mb-4">Perú · Neuralia369</p>
              <h1>Agentes IA para empresas en <span className="gradient-text">Perú</span></h1>
              <p className="mt-6 text-lg text-text-body">Creamos agentes especializados para seguimiento comercial, operación interna, documentos, comunicación y reporting con una lógica empresarial clara.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                {['Perú', 'Agentes IA', 'Ventas', 'Automatización'].map((item) => <span key={item} className="insight-chip">{item}</span>)}
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contacto" className="btn-primary" data-testid="agentes-peru-contact-cta">Quiero agentes IA<ArrowRight className="w-5 h-5" /></Link>
                <Link to="/agenda" className="btn-secondary" data-testid="agentes-peru-agenda-cta">Reservar llamada</Link>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {[
                { icon: Bot, title: 'Agentes especializados', copy: 'Cada agente ataca una función operativa concreta.' },
                { icon: MessageSquare, title: 'Comunicación', copy: 'Seguimiento y respuesta más consistente en varios canales.' },
                { icon: FileSearch, title: 'Documentos', copy: 'Procesado y organización de información clave.' },
                { icon: Workflow, title: 'Orquestación', copy: 'Todo coordinado desde una misma capa de control.' },
              ].map(({ icon: Icon, title, copy }) => (
                <div key={title} className="glass-card tracing-panel p-6" data-testid={`agentes-peru-card-${title}`}>
                  <Icon className="w-6 h-6 text-primary" />
                  <p className="mt-4 text-lg font-semibold text-text-heading">{title}</p>
                  <p className="mt-3 text-sm text-text-body">{copy}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
        <GeoSeoSections
          slug="agentes-peru"
          regionLabel="Perú"
          successCases={successCases}
          sectors={sectors}
          faqs={faqs}
          localSignals={localSignals}
        />
      </div>
    </>
  );
}